import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {Trigger, TriggerAction, TriggerSpecification} from "types/Trigger";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import Page from "components/Page";
import TriggerForm from "pages/Trigger/Form";
import {TriggerFormData} from "pages/Trigger/Form/TriggerForm";
import TriggerType from "types/TriggerType";
import {Beacon} from "types/Beacon";
import {PhysicalObject} from "types/PhysicalObject";
import {QrCode} from "types/QrCode";
import HardwareType from "types/HardwareType";
import {Option} from "components/Form/Select/Select";
import TriggerActionType from "types/TriggerActionType";
import {exhibitOption, facilityOption, messageOption, offerOption} from "services/options";
import {ActionFormData} from "pages/Trigger/Form/ActionEditor";
import {invalidate} from "services/query-client";
import {ZoneHardware} from "../../../types/Zone";
import {InteractionArea} from "../../../types/InteractionArea";

// Types ----

type Hardware = Beacon | PhysicalObject | QrCode | InteractionArea | ZoneHardware

// Helpers ----

// TODO: Make use of a UUID for this
export function uniqueHardwareId(item: Hardware): string {
    return `${item.type}.${item.id}`;
}

const hardwareToOption = (hardwareItem: Hardware) => {
    return {
        id: uniqueHardwareId(hardwareItem),
    }
}

const actionToForm = (action: TriggerAction, index: number): ActionFormData => {
    let entity = null

    switch(action.action_type){
        case TriggerActionType.EXHIBIT:
            entity = exhibitOption(action.exhibit)
            break

        case TriggerActionType.FACILITY:
            entity = facilityOption(action.facility)
            break

        case TriggerActionType.MESSAGE:
            entity = messageOption(action.message)
            break

        case TriggerActionType.OFFER:
            entity = offerOption(action.offer)
            break

        case TriggerActionType.URL:
            entity = action.url
            break
    }

    return {
        id: action.id,
        name: action.name,
        order: index + 1,
        action_type: action.action_type,
        entity: entity ?? undefined
    }
}

const triggerToForm = (trigger?: Trigger | null): TriggerFormData | null => {
    if (!trigger) return null;

    const hardware = trigger?.trigger_type === TriggerType.ACTIVATION? trigger.hardware.map(hardwareToOption) : [];
    const actions = trigger?.actions?.map(actionToForm)

    return Object.assign({...TriggerForm.defaults}, trigger, {hardware, actions}) as TriggerFormData
}

const entityId = (entity?: Option | string): number | undefined => {
    return entity? Number(typeof entity === 'string'? entity : entity.id) : undefined;
}

const entityString = (entity?: Option | string): string | undefined => {
    return entity? (typeof entity === 'string'? entity : entity.id).toString() : undefined;
}

const formToSpecification = (values: TriggerFormData): TriggerSpecification => ({
    name: values.name,
    trigger_type: values.trigger_type,
    hardware: values.hardware.map(hardware => {
        const parts = hardware.id.toString().split('.')
        return {type: parts[0] as HardwareType, id: parseInt(parts[1])}
    }),
    segment_id: values.segment_id,
    timings: values.timings,
    actions: values.actions.map(action => ({
        id: action.id,
        action_type: action.action_type,
        name: action.name,
        order: action.order,
        url: action.action_type === TriggerActionType.URL? entityString(action.entity) : undefined,
        exhibit_id: action.action_type === TriggerActionType.EXHIBIT? entityId(action.entity) : undefined,
        facility_id: action.action_type === TriggerActionType.FACILITY? entityId(action.entity) : undefined,
        offer_id: action.action_type === TriggerActionType.OFFER? entityId(action.entity) : undefined,
        message_id: action.action_type === TriggerActionType.MESSAGE? entityId(action.entity) : undefined,
    })),
    is_enabled: values.is_enabled,
})

// Component ----

function MutateTrigger() {
    const {id} = useParams()
    const {data: trigger, isLoading} = useQuery(
        ['triggers', {id}],
        () => ngage.triggers.get(Number(id)),
        {enabled: !!id}
    )

    const store = useMutation((data: TriggerSpecification) => ngage.triggers.store(data))
    const update = useMutation((data: TriggerSpecification) => ngage.triggers.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: TriggerFormData, {setSubmitting}: FormikHelpers<TriggerFormData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('triggers.all')

        setSubmitting(false)
        navigate('/triggers')
    }

    // Handle store mutation
    async function handleStore(values: TriggerFormData) {
        try {
            await store.mutateAsync(formToSpecification(values))
            notify("success", "Trigger Created", `The trigger has been created`)
        } catch (error) {
            notify("error", "Trigger Not Created", `The trigger could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: TriggerFormData) {
        try {
            await update.mutateAsync(formToSpecification(values))
            notify("success", "Trigger Updated", `The trigger has been updated`)
            await invalidate(['triggers', {id}])
        } catch (error) {
            notify("error", "Trigger Not Updated", `The trigger could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Engagement': '/', 'Triggers': '/triggers', 'New Trigger': '/triggers/new'}
    const editBreadcrumbs = {'Engagement': '/', 'Triggers': '/triggers', 'Edit Trigger': `/triggers/${id}/edit`}

    return (
        <Page title={id ? "Edit Trigger" : "New Trigger"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}
        >
            {<TriggerForm values={triggerToForm(trigger)} onSubmit={handleSubmit}/>}
        </Page>
    );
}

export default MutateTrigger;